import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import {
    Card, Typography
} from "@material-tailwind/react";
import Select from 'react-select';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { exportReportToCSV } from '../../../utils/expenseExport';
const { rootUrl } = require('../../../config/config');

const ExpenseReports = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('weekly');
    const [error, setError] = useState('');

    // Filters
    const [filters, setFilters] = useState({
        year: new Date().getFullYear(),
        week: '',
        month: '',
        plate_no: '',
        owner_code: '',
        category_id: '',
        is_invoiced_filter: 'all'
    });

    // Report data
    const [weeklyReport, setWeeklyReport] = useState(null);
    const [monthlyReport, setMonthlyReport] = useState(null);
    const [vehicleReport, setVehicleReport] = useState(null);
    const [ownerReport, setOwnerReport] = useState(null);
    const [trendsReport, setTrendsReport] = useState(null);
    const [approvalStats, setApprovalStats] = useState(null);

    // Reference data
    const [cars, setCars] = useState([]);
    const [owners, setOwners] = useState([]);
    const [, setCategories] = useState([]);
    const [currentWeekKey, setCurrentWeekKey] = useState('');
    const [validWeeks, setValidWeeks] = useState([]);

    const COLORS = ['#FF5733', '#33C3FF', '#33FF57', '#FFD133', '#D133FF', '#FF3380'];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchReferenceData();
        fetchWeekData();
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchReports();
    }, [activeTab, filters]);

    const fetchReferenceData = async () => {
        try {
            const [carsRes, ownersRes, categoriesRes] = await Promise.all([
                axios.get(`${rootUrl}/cars`, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                }),
                axios.get(`${rootUrl}/owners/list`, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                }),
                axios.get(`${rootUrl}/api/expenses/categories`, {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                })
            ]);

            setCars(carsRes.data.data || carsRes.data || []);
            setOwners(ownersRes.data || []);
            if (categoriesRes.data.success) {
                setCategories(categoriesRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching reference data:', error);
        }
    };

    const fetchWeekData = async () => {
        try {
            const response = await axios.get(`${rootUrl}/system/valid_weeks?past=24`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.data?.success) {
                const weeks = response.data.valid_weeks || [];
                const currentWeek = response.data.system_week || '';
                setValidWeeks(weeks);
                setCurrentWeekKey(currentWeek);
                if (!filters.week && currentWeek) {
                    setFilters((prev) => ({ ...prev, week: currentWeek }));
                }
            }
        } catch (error) {
            console.error('Error fetching week data:', error);
        }
    };

    const fetchReports = async () => {
        setLoading(true);
        setError('');
        try {
            switch (activeTab) {
                case 'weekly':
                    await fetchWeeklyReport();
                    break;
                case 'monthly':
                    await fetchMonthlyReport();
                    break;
                case 'vehicle':
                    await fetchVehicleReport();
                    break;
                case 'owner':
                    await fetchOwnerReport();
                    break;
                case 'trends':
                    await fetchTrendsReport();
                    break;
                case 'approval':
                    await fetchApprovalStats();
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error('Error fetching reports:', error);
        } finally {
            setLoading(false);
        }
    };

    const resolveWeekKey = () => {
        if (filters.week && String(filters.week).includes('_')) {
            return filters.week;
        }

        const year = parseInt(filters.year, 10);
        const week = parseInt(filters.week, 10);
        if (!Number.isNaN(year) && !Number.isNaN(week) && week > 0) {
            return `${year}_${String(week).padStart(2, '0')}`;
        }

        return currentWeekKey || '';
    };

    const fetchWeeklyReport = async () => {
        try {
            const weekKey = resolveWeekKey();
            const year = parseInt(filters.year, 10);

            let url;
            if (weekKey) {
                // Fetch specific week
                url = `${rootUrl}/api/expenses/reports/weekly/${weekKey}`;
            } else if (year) {
                // Fetch all weeks in selected year
                url = `${rootUrl}/api/expenses/reports/weekly-by-year/${year}`;
            } else {
                setWeeklyReport(null);
                setError('Please select a year.');
                return;
            }

            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.data.success) {
                setWeeklyReport(response.data.data || {});
                setError('');
            } else {
                setWeeklyReport(null);
                setError(response.data.message || 'Failed to load weekly report.');
            }
        } catch (error) {
            console.error('Error fetching weekly report:', error);
            setWeeklyReport(null);
            setError(error.response?.data?.message || 'Error loading weekly report. Please try again.');
        }
    };

    const fetchMonthlyReport = async () => {
        try {
            const year = parseInt(filters.year, 10) || new Date().getFullYear();
            const month = parseInt(filters.month, 10);

            let url;
            if (month) {
                // Fetch specific month
                url = `${rootUrl}/api/expenses/reports/monthly/${year}/${month}`;
            } else {
                // Fetch all months in selected year
                url = `${rootUrl}/api/expenses/reports/monthly-by-year/${year}`;
            }

            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.data.success) {
                setMonthlyReport(response.data.data || {});
                setError('');
            } else {
                setMonthlyReport(null);
                setError(response.data.message || 'Failed to load monthly report.');
            }
        } catch (error) {
            console.error('Error fetching monthly report:', error);
            setMonthlyReport(null);
            setError(error.response?.data?.message || 'Error loading monthly report. Please try again.');
        }
    };

    const fetchVehicleReport = async () => {
        try {
            // If no vehicle selected, fetch all vehicles grouped
            const url = filters.plate_no
                ? `${rootUrl}/api/expenses/reports/vehicle/${filters.plate_no}`
                : `${rootUrl}/api/expenses/reports/vehicles-grouped`;

            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.data.success) {
                setVehicleReport(response.data.data || {});
                setError('');
            } else {
                setVehicleReport(null);
                setError(response.data.message || 'Failed to load vehicle report.');
            }
        } catch (error) {
            console.error('Error fetching vehicle report:', error);
            setVehicleReport(null);
            setError(error.response?.data?.message || 'Error loading vehicle report. Please try again.');
        }
    };

    const fetchOwnerReport = async () => {
        try {
            // If no owner selected, fetch all owners grouped
            const url = filters.owner_code
                ? `${rootUrl}/api/expenses/reports/owner/${filters.owner_code}`
                : `${rootUrl}/api/expenses/reports/owners-grouped`;

            const response = await axios.get(url, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.data.success) {
                setOwnerReport(response.data.data || {});
                setError('');
            } else {
                setOwnerReport(null);
                setError(response.data.message || 'Failed to load owner report.');
            }
        } catch (error) {
            console.error('Error fetching owner report:', error);
            setOwnerReport(null);
            setError(error.response?.data?.message || 'Error loading owner report. Please try again.');
        }
    };

    const fetchTrendsReport = async () => {
        try {
            const params = { weeks: 12, limit: 12 };

            const response = await axios.get(`${rootUrl}/api/expenses/reports/trends`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
                params
            });

            if (response.data.success) {
                setTrendsReport(response.data.data || {});
                setError('');
            } else {
                setTrendsReport(null);
                setError(response.data.message || 'Failed to load trends report.');
            }
        } catch (error) {
            console.error('Error fetching trends report:', error);
            setTrendsReport(null);
            setError(error.response?.data?.message || 'Error loading trends report. Please try again.');
        }
    };

    const fetchApprovalStats = async () => {
        try {
            const weekKey = resolveWeekKey();
            const params = {};
            if (weekKey) params.week_key = weekKey;

            const response = await axios.get(`${rootUrl}/api/expenses/reports/approvals`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
                params
            });

            if (response.data.success) {
                setApprovalStats(response.data.data || {});
                setError('');
            } else {
                setApprovalStats(null);
                setError(response.data.message || 'Failed to load approval stats.');
            }
        } catch (error) {
            console.error('Error fetching approval stats:', error);
            setApprovalStats(null);
            setError(error.response?.data?.message || 'Error loading approval stats. Please try again.');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getMonthlySummary = () => {
        const summary = monthlyReport?.summary || {};
        const weekly = monthlyReport?.weekly_breakdown || [];
        const totals = weekly.map((item) => parseFloat(item.total_amount || 0));

        const totalWeeks = totals.length;
        const totalAmount = totals.reduce((acc, value) => acc + value, 0);
        const avgPerWeek = totalWeeks ? totalAmount / totalWeeks : 0;
        const highestWeek = totalWeeks ? Math.max(...totals) : 0;
        const lowestWeek = totalWeeks ? Math.min(...totals) : 0;

        return {
            total_amount: parseFloat(summary.total_amount || 0),
            avg_per_week: avgPerWeek,
            highest_week: highestWeek,
            lowest_week: lowestWeek
        };
    };

    const monthlySummary = getMonthlySummary();

    const downloadReport = () => {
        let data = null;
        let filename = `expense_report_${activeTab}`;

        switch (activeTab) {
            case 'weekly':
                data = weeklyReport;
                break;
            case 'monthly':
                data = monthlyReport;
                break;
            case 'vehicle':
                data = vehicleReport;
                break;
            case 'owner':
                data = ownerReport;
                break;
            case 'trends':
                data = trendsReport;
                break;
            case 'approval':
                data = approvalStats;
                break;
            default:
                break;
        }

        if (data) {
            exportReportToCSV(data, activeTab, filename);
        } else {
            alert('No data available to export');
        }
    };

    const weekOptions = [{ value: '', label: 'Current Week' }, ...validWeeks.map((weekKey) => ({ value: weekKey, label: weekKey }))];
    const monthOptions = [{ value: '', label: 'All Months' }, ...[...Array(12)].map((_, i) => ({
        value: (i + 1).toString(),
        label: new Date(2000, i).toLocaleString('default', { month: 'long' })
    }))];
    const vehicleOptions = [{ value: '', label: 'All Vehicles' }, ...cars.map((car) => ({ value: car.plate_no, label: car.plate_no }))];
    const filteredOwners = filters.is_invoiced_filter === 'invoiced'
        ? owners.filter(owner => owner.is_invoiced === true)
        : owners;
    const ownerOptions = [{ value: '', label: 'All Owners' }, ...filteredOwners.map((owner) => ({ value: owner.code, label: owner.name }))];
    const invoicedOptions = [
        { value: 'all', label: 'All Owners' },
        { value: 'invoiced', label: 'Owners with Invoices Only' }
    ];
    const selectMenuPortalTarget = typeof document !== 'undefined' ? document.body : null;
    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: 44,
            height: 44,
            borderColor: '#d1d5db',
            boxShadow: 'none',
            ':hover': { borderColor: '#9ca3af' }
        }),
        valueContainer: (base) => ({ ...base, height: 44, padding: '0 8px' }),
        input: (base) => ({ ...base, margin: 0, padding: 0 }),
        indicatorsContainer: (base) => ({ ...base, height: 44 }),
        menuPortal: (base) => ({ ...base, zIndex: 50 })
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Expense Reports & Analytics</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Comprehensive expense analysis and insights
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {/* Filters */}
                <Card className="mb-6 p-6">
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="w-full sm:w-52">
                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Year</label>
                            <input
                                type="number"
                                className="w-full h-11 border border-gray-300 rounded-md px-3 py-2"
                                value={filters.year}
                                onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                            />
                        </div>

                        {activeTab === 'weekly' && (
                            <div className="w-full sm:w-52">
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Week</label>
                                <Select
                                    value={weekOptions.find((opt) => opt.value === filters.week) || weekOptions[0]}
                                    onChange={(option) => setFilters({ ...filters, week: option?.value || '' })}
                                    options={weekOptions}
                                    isSearchable
                                    styles={selectStyles}
                                    menuPortalTarget={selectMenuPortalTarget || undefined}
                                />
                            </div>
                        )}

                        {activeTab === 'monthly' && (
                            <div className="w-full sm:w-52">
                                <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Month</label>
                                <Select
                                    value={monthOptions.find((opt) => opt.value === filters.month) || monthOptions[0]}
                                    onChange={(option) => setFilters({ ...filters, month: option?.value || '' })}
                                    options={monthOptions}
                                    isSearchable
                                    styles={selectStyles}
                                    menuPortalTarget={selectMenuPortalTarget || undefined}
                                />
                            </div>
                        )}

                        {(activeTab === 'vehicle' || activeTab === 'owner') && (
                            <>
                                {activeTab === 'vehicle' && (
                                    <div className="w-full sm:w-56">
                                        <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Vehicle</label>
                                        <Select
                                            value={vehicleOptions.find((opt) => opt.value === filters.plate_no) || vehicleOptions[0]}
                                            onChange={(option) => setFilters({ ...filters, plate_no: option?.value || '' })}
                                            options={vehicleOptions}
                                            isSearchable
                                            styles={selectStyles}
                                            menuPortalTarget={selectMenuPortalTarget || undefined}
                                        />
                                    </div>
                                )}

                                {activeTab === 'owner' && (
                                    <>
                                        <div className="w-full sm:w-60">
                                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Invoice Status</label>
                                            <Select
                                                value={invoicedOptions.find((opt) => opt.value === filters.is_invoiced_filter) || invoicedOptions[0]}
                                                onChange={(option) => setFilters({ ...filters, is_invoiced_filter: option?.value || 'all', owner_code: '' })}
                                                options={invoicedOptions}
                                                isSearchable
                                                styles={selectStyles}
                                                menuPortalTarget={selectMenuPortalTarget || undefined}
                                            />
                                        </div>
                                        <div className="w-full sm:w-60">
                                            <label className="block text-sm font-semibold text-[#2a2e30] mb-2">Owner</label>
                                            <Select
                                                value={ownerOptions.find((opt) => opt.value === filters.owner_code) || ownerOptions[0]}
                                                onChange={(option) => setFilters({ ...filters, owner_code: option?.value || '' })}
                                                options={ownerOptions}
                                                isSearchable
                                                styles={selectStyles}
                                                menuPortalTarget={selectMenuPortalTarget || undefined}
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        <div className="flex gap-2 w-full sm:w-auto sm:ml-auto">
                            <button
                                onClick={fetchReports}
                                className="h-11 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 shadow-none flex items-center justify-center gap-2"
                            >
                                <FontAwesomeIcon icon={faSyncAlt} />
                                Refresh
                            </button>

                            <button
                                onClick={downloadReport}
                                className="h-11 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-4"
                            >
                                <span className="flex items-center justify-center gap-2">
                                    <FontAwesomeIcon icon={faDownload} />
                                    Export
                                </span>
                            </button>
                        </div>
                    </div>
                </Card>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex border-b border-gray-200 mb-6">
                        <button
                            onClick={() => setActiveTab('weekly')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'weekly' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                        >
                            Weekly
                        </button>
                        <button
                            onClick={() => setActiveTab('monthly')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'monthly' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setActiveTab('vehicle')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'vehicle' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                        >
                            By Vehicle
                        </button>
                        <button
                            onClick={() => setActiveTab('owner')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'owner' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                        >
                            By Owner
                        </button>
                        <button
                            onClick={() => setActiveTab('trends')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'trends' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                        >
                            Trends
                        </button>
                        <button
                            onClick={() => setActiveTab('approval')}
                            className={`px-6 py-3 font-semibold text-base transition-all border-b-2 ${activeTab === 'approval' ? 'border-[#FF5733] text-[#FF5733]' : 'border-transparent text-gray-600'}`}
                        >
                            Approvals
                        </button>
                    </div>

                    {/* Report Content */}
                    {loading ? (
                        <Card className="p-8 text-center">
                            <FontAwesomeIcon icon={faSyncAlt} spin size="3x" className="text-[#FF5733] mb-4" />
                            <Typography>Loading report data...</Typography>
                        </Card>
                    ) : (
                        <>
                            {error && (
                                <Card className="mb-4 p-4">
                                    <Typography className="text-red-500">{error}</Typography>
                                </Card>
                            )}
                            {/* Weekly Report */}
                            {activeTab === 'weekly' && weeklyReport && (
                                <div className="space-y-6">
                                    {/* Summary Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                Total Expenses
                                            </Typography>
                                            <Typography variant="h4" className="text-[#FF5733] font-bold">
                                                {formatCurrency(weeklyReport.summary?.total_amount || 0)}
                                            </Typography>
                                        </Card>
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                {weeklyReport.weeks ? 'Week Count' : 'Vehicle Expenses'}
                                            </Typography>
                                            <Typography variant="h4" className={`${weeklyReport.weeks ? 'text-gray-800' : 'text-blue-600'} font-bold`}>
                                                {weeklyReport.weeks
                                                    ? (weeklyReport.summary?.week_count || 0)
                                                    : formatCurrency(weeklyReport.summary?.vehicle_expenses || 0)
                                                }
                                            </Typography>
                                        </Card>
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                {weeklyReport.weeks ? 'Average per Week' : 'Office Expenses'}
                                            </Typography>
                                            <Typography variant="h4" className={`${weeklyReport.weeks ? 'text-blue-600' : 'text-purple-600'} font-bold`}>
                                                {weeklyReport.weeks
                                                    ? formatCurrency(weeklyReport.summary?.average_amount || 0)
                                                    : formatCurrency(weeklyReport.summary?.office_expenses || 0)
                                                }
                                            </Typography>
                                        </Card>
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                Transaction Count
                                            </Typography>
                                            <Typography variant="h4" className="text-gray-800 font-bold">
                                                {weeklyReport.summary?.total_expenses || 0}
                                            </Typography>
                                        </Card>
                                    </div>

                                    {/* Yearly Weeks Table */}
                                    {weeklyReport.weeks && weeklyReport.weeks.length > 0 && (
                                        <Card className="overflow-x-auto">
                                            <table className="w-full table-auto">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="p-4 text-left">Week</th>
                                                        <th className="p-4 text-right">Total Amount</th>
                                                        <th className="p-4 text-right">Expense Count</th>
                                                        <th className="p-4 text-right">Average</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {weeklyReport.weeks.map((item) => (
                                                        <tr key={item.week_key} className="border-b hover:bg-gray-50">
                                                            <td className="p-4 font-semibold">{item.week_key}</td>
                                                            <td className="p-4 text-right font-bold text-[#FF5733]">
                                                                {formatCurrency(item.total_amount)}
                                                            </td>
                                                            <td className="p-4 text-right">{item.total_expenses}</td>
                                                            <td className="p-4 text-right">{formatCurrency(item.average_amount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Card>
                                    )}

                                    {/* Category Breakdown - Single Week View */}
                                    {weeklyReport.by_category && weeklyReport.by_category.length > 0 && (
                                        <Card className="p-6">
                                            <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                                Expenses by Category
                                            </Typography>
                                            <ResponsiveContainer width="100%" height={400}>
                                                <BarChart data={weeklyReport.by_category}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="category_name" angle={-45} textAnchor="end" height={100} />
                                                    <YAxis />
                                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                                    <Legend />
                                                    <Bar dataKey="total_amount" fill="#FF5733" name="Amount" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Card>
                                    )}
                                </div>
                            )}

                            {/* Weekly Report - No Data Fallback */}
                            {activeTab === 'weekly' && !weeklyReport && !loading && !error && (
                                <Card className="p-8 text-center">
                                    <Typography className="text-gray-600">No weekly report data available.</Typography>
                                </Card>
                            )}

                            {/* Monthly Report */}
                            {activeTab === 'monthly' && monthlyReport && (
                                <div className="space-y-6">
                                    {/* Summary Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                Total Expenses
                                            </Typography>
                                            <Typography variant="h4" className="text-[#FF5733] font-bold">
                                                {formatCurrency(monthlyReport.summary?.total_amount || monthlySummary.total_amount)}
                                            </Typography>
                                        </Card>
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                {monthlyReport.months ? 'Month Count' : 'Average per Week'}
                                            </Typography>
                                            <Typography variant="h4" className="text-blue-600 font-bold">
                                                {monthlyReport.months
                                                    ? (monthlyReport.summary?.month_count || 0)
                                                    : formatCurrency(monthlySummary.avg_per_week)
                                                }
                                            </Typography>
                                        </Card>
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                {monthlyReport.months ? 'Average per Month' : 'Highest Week'}
                                            </Typography>
                                            <Typography variant="h4" className={`${monthlyReport.months ? 'text-blue-600' : 'text-red-600'} font-bold`}>
                                                {monthlyReport.months
                                                    ? formatCurrency(monthlyReport.summary?.average_amount || 0)
                                                    : formatCurrency(monthlySummary.highest_week)
                                                }
                                            </Typography>
                                        </Card>
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                {monthlyReport.months ? 'Total Transactions' : 'Lowest Week'}
                                            </Typography>
                                            <Typography variant="h4" className={`${monthlyReport.months ? 'text-gray-800' : 'text-green-600'} font-bold`}>
                                                {monthlyReport.months
                                                    ? (monthlyReport.summary?.total_expenses || 0)
                                                    : formatCurrency(monthlySummary.lowest_week)
                                                }
                                            </Typography>
                                        </Card>
                                    </div>

                                    {/* Yearly Months Table */}
                                    {monthlyReport.months && monthlyReport.months.length > 0 && (
                                        <Card className="overflow-x-auto">
                                            <table className="w-full table-auto">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="p-4 text-left">Month</th>
                                                        <th className="p-4 text-right">Total Amount</th>
                                                        <th className="p-4 text-right">Expense Count</th>
                                                        <th className="p-4 text-right">Average</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {monthlyReport.months.map((item) => (
                                                        <tr key={item.month} className="border-b hover:bg-gray-50">
                                                            <td className="p-4 font-semibold">{item.month_name}</td>
                                                            <td className="p-4 text-right font-bold text-[#FF5733]">
                                                                {formatCurrency(item.total_amount)}
                                                            </td>
                                                            <td className="p-4 text-right">{item.total_expenses}</td>
                                                            <td className="p-4 text-right">{formatCurrency(item.average_amount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Card>
                                    )}

                                    {/* Weekly Trend - Single Month View */}
                                    {monthlyReport.weekly_breakdown && monthlyReport.weekly_breakdown.length > 0 && (
                                        <Card className="p-6">
                                            <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                                Weekly Trend
                                            </Typography>
                                            <ResponsiveContainer width="100%" height={400}>
                                                <LineChart data={monthlyReport.weekly_breakdown}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="week_key" />
                                                    <YAxis />
                                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                                    <Legend />
                                                    <Line type="monotone" dataKey="total_amount" stroke="#FF5733" strokeWidth={2} name="Total" />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Card>
                                    )}
                                </div>
                            )}

                            {/* Monthly Report - No Data Fallback */}
                            {activeTab === 'monthly' && !monthlyReport && !loading && !error && (
                                <Card className="p-8 text-center">
                                    <Typography className="text-gray-600">No monthly report data available.</Typography>
                                </Card>
                            )}

                            {/* Vehicle Report */}
                            {activeTab === 'vehicle' && vehicleReport && (
                                <div className="space-y-6">
                                    {vehicleReport.summary ? (
                                        <>
                                            {/* Summary Cards */}
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                <Card className="p-4">
                                                    <Typography variant="small" className="text-gray-600">Total Expenses</Typography>
                                                    <Typography variant="h4" className="text-[#FF5733] font-bold">
                                                        {formatCurrency(vehicleReport.summary.total_amount)}
                                                    </Typography>
                                                </Card>
                                                <Card className="p-4">
                                                    <Typography variant="small" className="text-gray-600">
                                                        {filters.plate_no ? 'Transaction Count' : 'Vehicle Count'}
                                                    </Typography>
                                                    <Typography variant="h4" className="text-gray-800 font-bold">
                                                        {filters.plate_no ? (vehicleReport.summary.total_expenses || 0) : (vehicleReport.summary.vehicle_count || 0)}
                                                    </Typography>
                                                </Card>
                                                <Card className="p-4">
                                                    <Typography variant="small" className="text-gray-600">Average Expense</Typography>
                                                    <Typography variant="h4" className="text-blue-600 font-bold">
                                                        {formatCurrency(vehicleReport.summary.average_expense)}
                                                    </Typography>
                                                </Card>
                                                {filters.plate_no && (
                                                    <Card className="p-4">
                                                        <Typography variant="small" className="text-gray-600">Expense Range</Typography>
                                                        <Typography variant="h6" className="text-gray-800 font-semibold">
                                                            {formatDate(vehicleReport.summary.first_expense_date)} - {formatDate(vehicleReport.summary.last_expense_date)}
                                                        </Typography>
                                                    </Card>
                                                )}
                                            </div>

                                            {/* Grouped vehicles table (when no specific vehicle selected) */}
                                            {vehicleReport.vehicles && vehicleReport.vehicles.length > 0 && (
                                                <Card className="overflow-x-auto">
                                                    <table className="w-full table-auto">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="p-4 text-left">Vehicle</th>
                                                                <th className="p-4 text-left">Owner</th>
                                                                <th className="p-4 text-right">Total Amount</th>
                                                                <th className="p-4 text-right">Expense Count</th>
                                                                <th className="p-4 text-right">Avg Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {vehicleReport.vehicles.map((item) => (
                                                                <tr key={`${item.plate_no}-${item.owner_code}`} className="border-b hover:bg-gray-50">
                                                                    <td className="p-4 font-semibold">{item.plate_no}</td>
                                                                    <td className="p-4">{item.owner_name || 'N/A'}</td>
                                                                    <td className="p-4 text-right font-semibold text-[#FF5733]">
                                                                        {formatCurrency(item.total_amount)}
                                                                    </td>
                                                                    <td className="p-4 text-right">{item.total_expenses}</td>
                                                                    <td className="p-4 text-right">{formatCurrency(item.average_expense)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </Card>
                                            )}

                                            {vehicleReport.by_category && vehicleReport.by_category.length > 0 && (
                                                <Card className="p-6">
                                                    <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                                        Expenses by Category
                                                    </Typography>
                                                    <ResponsiveContainer width="100%" height={400}>
                                                        <BarChart data={vehicleReport.by_category}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="category_name" angle={-45} textAnchor="end" height={100} />
                                                            <YAxis />
                                                            <Tooltip formatter={(value) => formatCurrency(value)} />
                                                            <Legend />
                                                            <Bar dataKey="total_amount" fill="#FF5733" name="Amount" />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </Card>
                                            )}

                                            {vehicleReport.recent_expenses && vehicleReport.recent_expenses.length > 0 && (
                                                <Card className="overflow-x-auto">
                                                    <table className="w-full table-auto">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="p-4 text-left">Expense No</th>
                                                                <th className="p-4 text-left">Category</th>
                                                                <th className="p-4 text-left">Date</th>
                                                                <th className="p-4 text-right">Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {vehicleReport.recent_expenses.map((item) => (
                                                                <tr key={item.id} className="border-b hover:bg-gray-50">
                                                                    <td className="p-4 font-semibold">{item.expense_no}</td>
                                                                    <td className="p-4">{item.category_name || 'N/A'}</td>
                                                                    <td className="p-4">{formatDate(item.expense_date)}</td>
                                                                    <td className="p-4 text-right font-semibold text-[#FF5733]">
                                                                        {formatCurrency(item.amount)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </Card>
                                            )}
                                        </>
                                    ) : (
                                        <Card className="p-8 text-center">
                                            <Typography className="text-gray-600">No vehicle data available</Typography>
                                        </Card>
                                    )}
                                </div>
                            )}

                            {/* Vehicle Report - No Data Fallback */}
                            {activeTab === 'vehicle' && !vehicleReport && !loading && !error && (
                                <Card className="p-8 text-center">
                                    <Typography className="text-gray-600">No vehicle report data available.</Typography>
                                </Card>
                            )}

                            {/* Owner Report */}
                            {activeTab === 'owner' && ownerReport && (
                                <div className="space-y-6">
                                    {ownerReport.summary ? (
                                        <>
                                            {/* Summary Cards */}
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                <Card className="p-4">
                                                    <Typography variant="small" className="text-gray-600">Total Expenses</Typography>
                                                    <Typography variant="h4" className="text-[#FF5733] font-bold">
                                                        {formatCurrency(ownerReport.summary.total_amount)}
                                                    </Typography>
                                                </Card>
                                                <Card className="p-4">
                                                    <Typography variant="small" className="text-gray-600">
                                                        {filters.owner_code ? 'Vehicle Count' : 'Owner Count'}
                                                    </Typography>
                                                    <Typography variant="h4" className="text-gray-800 font-bold">
                                                        {filters.owner_code ? (ownerReport.summary.vehicle_count || 0) : (ownerReport.summary.owner_count || 0)}
                                                    </Typography>
                                                </Card>
                                                <Card className="p-4">
                                                    <Typography variant="small" className="text-gray-600">Transaction Count</Typography>
                                                    <Typography variant="h4" className="text-gray-800 font-bold">
                                                        {ownerReport.summary.total_expenses || 0}
                                                    </Typography>
                                                </Card>
                                                {filters.owner_code && (
                                                    <Card className="p-4">
                                                        <Typography variant="small" className="text-gray-600">Pending Reimbursement</Typography>
                                                        <Typography variant="h4" className="text-amber-600 font-bold">
                                                            {formatCurrency(ownerReport.summary.pending_reimbursement)}
                                                        </Typography>
                                                    </Card>
                                                )}
                                            </div>

                                            {/* Grouped owners table (when no specific owner selected) */}
                                            {ownerReport.owners && ownerReport.owners.length > 0 && (
                                                <Card className="overflow-x-auto">
                                                    <table className="w-full table-auto">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="p-4 text-left">Owner Name</th>
                                                                <th className="p-4 text-left">Owner Code</th>
                                                                <th className="p-4 text-left">Phone</th>
                                                                <th className="p-4 text-right">Total Amount</th>
                                                                <th className="p-4 text-right">Expense Count</th>
                                                                <th className="p-4 text-right">Avg Amount</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {ownerReport.owners.map((item) => (
                                                                <tr key={item.owner_code} className="border-b hover:bg-gray-50">
                                                                    <td className="p-4 font-semibold">{item.owner_name}</td>
                                                                    <td className="p-4">{item.owner_code}</td>
                                                                    <td className="p-4">{item.owner_phone || 'N/A'}</td>
                                                                    <td className="p-4 text-right font-bold text-[#FF5733]">
                                                                        {formatCurrency(item.total_amount)}
                                                                    </td>
                                                                    <td className="p-4 text-right">{item.total_expenses}</td>
                                                                    <td className="p-4 text-right">{formatCurrency(item.average_expense)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </Card>
                                            )}

                                            {ownerReport.by_vehicle && ownerReport.by_vehicle.length > 0 && (
                                                <Card className="overflow-x-auto">
                                                    <table className="w-full table-auto">
                                                        <thead className="bg-gray-100">
                                                            <tr>
                                                                <th className="p-4 text-left">Vehicle</th>
                                                                <th className="p-4 text-right">Total Expenses</th>
                                                                <th className="p-4 text-right">Transaction Count</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {ownerReport.by_vehicle.map((item) => (
                                                                <tr key={item.plate_no} className="border-b hover:bg-gray-50">
                                                                    <td className="p-4 font-semibold">{item.plate_no}</td>
                                                                    <td className="p-4 text-right font-bold text-[#FF5733]">
                                                                        {formatCurrency(item.total_amount)}
                                                                    </td>
                                                                    <td className="p-4 text-right">{item.expense_count}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </Card>
                                            )}

                                            {ownerReport.by_category && ownerReport.by_category.length > 0 && (
                                                <Card className="p-6">
                                                    <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                                        Expenses by Category
                                                    </Typography>
                                                    <ResponsiveContainer width="100%" height={400}>
                                                        <BarChart data={ownerReport.by_category}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="category_name" angle={-45} textAnchor="end" height={100} />
                                                            <YAxis />
                                                            <Tooltip formatter={(value) => formatCurrency(value)} />
                                                            <Legend />
                                                            <Bar dataKey="total_amount" fill="#FF5733" name="Amount" />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </Card>
                                            )}
                                        </>
                                    ) : (
                                        <Card className="p-8 text-center">
                                            <Typography className="text-gray-600">No owner data available</Typography>
                                        </Card>
                                    )}
                                </div>
                            )}

                            {/* Owner Report - No Data Fallback */}
                            {activeTab === 'owner' && !ownerReport && !loading && !error && (
                                <Card className="p-8 text-center">
                                    <Typography className="text-gray-600">No owner report data available.</Typography>
                                </Card>
                            )}

                            {/* Trends Report */}
                            {activeTab === 'trends' && trendsReport && (
                                <div className="space-y-6">
                                    <Card className="p-6">
                                        <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                            Recent Expense Trends
                                        </Typography>
                                        <ResponsiveContainer width="100%" height={400}>
                                            <LineChart data={trendsReport.weekly_trend || []}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="week_key" />
                                                <YAxis />
                                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                                <Legend />
                                                <Line type="monotone" dataKey="total_amount" stroke="#FF5733" strokeWidth={2} name="Total" />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </Card>

                                    {trendsReport.category_distribution && trendsReport.category_distribution.length > 0 && (
                                        <Card className="p-6">
                                            <Typography variant="h5" className="text-gray-800 font-semibold mb-4">
                                                Category Distribution
                                            </Typography>
                                            <ResponsiveContainer width="100%" height={350}>
                                                <PieChart>
                                                    <Pie
                                                        data={trendsReport.category_distribution}
                                                        dataKey="total_amount"
                                                        nameKey="category_name"
                                                        outerRadius={140}
                                                    >
                                                        {trendsReport.category_distribution.map((_, index) => (
                                                            <Cell key={`cat-${index}`} fill={COLORS[index % COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                                    <Legend />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </Card>
                                    )}
                                </div>
                            )}

                            {/* Trends Report - No Data Fallback */}
                            {activeTab === 'trends' && !trendsReport && !loading && !error && (
                                <Card className="p-8 text-center">
                                    <Typography className="text-gray-600">No trends data available.</Typography>
                                </Card>
                            )}

                            {/* Approval Stats */}
                            {activeTab === 'approval' && approvalStats && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                Total Approved
                                            </Typography>
                                            <Typography variant="h4" className="text-green-600 font-bold">
                                                {formatCurrency(approvalStats.summary?.approved_amount || 0)}
                                            </Typography>
                                            <Typography variant="small" className="text-gray-500">
                                                {approvalStats.summary?.approved_count || 0} transactions
                                            </Typography>
                                        </Card>
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                Pending Approval
                                            </Typography>
                                            <Typography variant="h4" className="text-amber-600 font-bold">
                                                {formatCurrency(approvalStats.summary?.pending_amount || 0)}
                                            </Typography>
                                            <Typography variant="small" className="text-gray-500">
                                                {approvalStats.summary?.pending_count || 0} transactions
                                            </Typography>
                                        </Card>
                                        <Card className="p-4">
                                            <Typography variant="small" className="text-gray-600">
                                                Rejected
                                            </Typography>
                                            <Typography variant="h4" className="text-red-600 font-bold">
                                                {formatCurrency(approvalStats.summary?.rejected_amount || 0)}
                                            </Typography>
                                            <Typography variant="small" className="text-gray-500">
                                                {approvalStats.summary?.rejected_count || 0} transactions
                                            </Typography>
                                        </Card>
                                    </div>

                                    {approvalStats.by_approver && approvalStats.by_approver.length > 0 && (
                                        <Card className="overflow-x-auto">
                                            <table className="w-full table-auto">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="p-4 text-left">Approver</th>
                                                        <th className="p-4 text-right">Approval Count</th>
                                                        <th className="p-4 text-right">Total Approved</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {approvalStats.by_approver.map((item, index) => (
                                                        <tr key={`${item.approved_by || 'approver'}-${index}`} className="border-b hover:bg-gray-50">
                                                            <td className="p-4 font-semibold">{item.approved_by || 'N/A'}</td>
                                                            <td className="p-4 text-right">{item.approval_count}</td>
                                                            <td className="p-4 text-right font-semibold text-[#FF5733]">
                                                                {formatCurrency(item.total_approved_amount)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Card>
                                    )}

                                    {approvalStats.pending_expenses && approvalStats.pending_expenses.length > 0 && (
                                        <Card className="overflow-x-auto">
                                            <div className="p-4 border-b border-gray-200">
                                                <Typography variant="h6" className="text-gray-800 font-bold">
                                                    Pending Expenses for Approval
                                                </Typography>
                                            </div>
                                            <table className="w-full table-auto">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="p-4 text-left">Expense No</th>
                                                        <th className="p-4 text-left">Date</th>
                                                        <th className="p-4 text-left">Vehicle</th>
                                                        <th className="p-4 text-left">Category</th>
                                                        <th className="p-4 text-left">Description</th>
                                                        <th className="p-4 text-right">Amount</th>
                                                        <th className="p-4 text-left">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {approvalStats.pending_expenses.map((expense, index) => (
                                                        <tr key={`${expense.id}-${index}`} className="border-b hover:bg-gray-50">
                                                            <td className="p-4 font-semibold text-[#FF5733]">{expense.expense_no}</td>
                                                            <td className="p-4 text-sm">
                                                                {expense.expense_date ?
                                                                    new Date(expense.expense_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                                    : 'N/A'}
                                                            </td>
                                                            <td className="p-4 text-sm font-semibold">{expense.plate_no || 'N/A'}</td>
                                                            <td className="p-4 text-sm">{expense.category_name || 'N/A'}</td>
                                                            <td className="p-4 text-sm text-gray-700">{expense.description || '-'}</td>
                                                            <td className="p-4 text-right font-semibold">{formatCurrency(expense.amount)}</td>
                                                            <td className="p-4">
                                                                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-semibold">
                                                                    {expense.approval_status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Card>
                                    )}
                                </div>
                            )}

                            {/* Approval Stats - No Data Fallback */}
                            {activeTab === 'approval' && !approvalStats && !loading && !error && (
                                <Card className="p-8 text-center">
                                    <Typography className="text-gray-600">No approval statistics available.</Typography>
                                </Card>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExpenseReports;
